import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from "@angular/forms";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Store } from "@ngrx/store";
import { filter, take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import {
  selectOrganizationID,
  selectOrganizationLegacyId,
  selectOrganizationName,
} from "@store/selectors/add-organization/add-organization.selectors";
import { AttachmentUploadData, CreateTicketRequest, TicketCategory } from "@models/ticket-data.model";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import { selectTicketCategories } from "@store/selectors/issue-tracker/issue-tracker.selectors";
import { selectOrgEMRId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { Actions, ofType } from "@ngrx/effects";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SupportTicketConstants } from "../../../constants/support-ticket";
import {
  selectImpactedMeasureOptions,
  ImpactedMeasureOption,
} from "@store/selectors/dashboard/dashboard.selectors";
import { PowerBIService } from "@services/power-bi.service";
import { PowerBIReportName } from "@models/powerBIEmbed.model";

const MISSING_MEASURES_CATEGORY = "missing measure(s)";
const INCORRECT_CALCULATION_CATEGORY = "incorrect measure calculation";
const BI_REPORTS_CATEGORY = "bi reports";

export type MeasureFieldMode = "none" | "multi" | "single";

export interface TicketModalData {
  organizationId?: number;
  defaultCategoryName?: string;
  createdFromPatientTable?: boolean;
  lockCategory?: boolean;
  measureCode?: string;
  patientId?: number;
}

export interface AttachmentFile {
  file: File;
  name: string;
  size: number;
  type: string;
  dataEnrichmentDocumentType: string;
  dataEnrichmentDocumentTypeOther: string;
  dataEnrichmentDocumentDate: string;
}

@Component({
  selector: "app-add-ticket-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: "./add-ticket-modal.component.html",
  styleUrls: ["./add-ticket-modal.component.css"],
})
export class AddTicketModalComponent implements OnInit, OnDestroy {
  ticketForm!: FormGroup;
  includePhiSection = false;
  attachments: AttachmentFile[] = [];
  fileErrors: string[] = [];
  categories: TicketCategory[] = [];
  orgId: number = 0;
  orgEMRId: number = 0;
  isDragOver = false;
  measureFieldMode: MeasureFieldMode = "none";
  isBiReportsCategory = false;
  impactedMeasureOptions: ImpactedMeasureOption[] = [];
  availableReports: PowerBIReportName[] = [];
  isLoadingReports = false;
  measureSearchControl = new FormControl<string>("");
  private destroy$ = new Subject<void>();
  readonly MAX_FILE_SIZE = SupportTicketConstants.MAX_FILE_SIZE;
  readonly SUPPORTED_FILE_TYPES = SupportTicketConstants.SUPPORTED_FILE_TYPES;
  readonly FILE_ACCEPT = SupportTicketConstants.FILE_ACCEPT;
  readonly documentTypes = SupportTicketConstants.DOCUMENT_TYPES;

  readonly VALIDATION_LIMITS = {
    SUMMARY_MAX: 256,
    DESCRIPTION_MAX: 1000,
    PHI_DESCRIPTION_MAX: 1000,
  };

  constructor(
    public dialogRef: MatDialogRef<AddTicketModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TicketModalData,
    private store: Store,
    private fb: FormBuilder,
    private actions$: Actions,
    private snackBar: MatSnackBar,
    private powerBIService: PowerBIService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.store.dispatch(IssueTrackerActions.getTicketCategories());

this.actions$
  .pipe(
    ofType(IssueTrackerActions.createTicketSuccess),
    takeUntil(this.destroy$)
  )
  .subscribe(({ ticket }) => {
    this.snackBar.open(`Ticket "${ticket.id}" created successfully!`, "Close", {
      duration: 4000,
    });
    this.dialogRef.close(true);
  });

    this.store
      .select(selectActiveUser)
      .pipe(take(1))
      .subscribe((user) => {
        if (user) {
          const fullName =
            user.displayName ||
            user.loggedInUser?.firstName + " " + user.loggedInUser?.lastName ||
            "";
          const emailAddress = user.email || "";

          this.ticketForm.patchValue({
            fullName,
            emailAddress,
          });
        }
      });

    this.store
      .select(selectOrganizationName)
      .pipe(take(1))
      .subscribe((orgName) => {
        this.ticketForm.patchValue({
          organizationName: orgName || "",
        });
      });

    this.store
      .select(selectOrganizationID)
      .pipe(take(1))
      .subscribe((id) => {
        this.orgId = parseInt(id);
      });

    this.store
      .select(selectOrgEMRId)
      .pipe(take(1))
      .subscribe((id) => {
        this.orgEMRId = parseInt(id);
      });

    this.store
      .select(selectOrganizationLegacyId)
      .pipe(take(1))
      .subscribe((orgId) => {
        this.ticketForm.patchValue({
          legacyOrganizationId: parseInt(String(orgId || "0")),
        });
      });

    this.store
      .select(selectTicketCategories)
      .pipe(
        filter((categories) => categories.length > 0),
        take(1)
      )
      .subscribe((categories) => {
        this.categories = this.sortCategoriesWithOtherLast(categories);
      });

    if (this.data.createdFromPatientTable && this.data.lockCategory) {
      this.setAndLockMeasureMappingCategory();
    } else {
      this.store
        .select(selectImpactedMeasureOptions)
        .pipe(takeUntil(this.destroy$))
        .subscribe((options) => {
          this.impactedMeasureOptions = options;
        });

      this.ticketForm
        .get("category")
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((categoryName: string) => {
          this.onCategoryChanged(categoryName);
        });
    }
  }

  private onCategoryChanged(categoryName: string): void {
    const normalizedCategoryName = (categoryName || "").toLowerCase();
    const impactedMeasureCodesControl = this.ticketForm.get("impactedMeasureCodes");
    const impactedMeasureCodeControl = this.ticketForm.get("impactedMeasureCode");

    if (normalizedCategoryName === MISSING_MEASURES_CATEGORY) {
      this.measureFieldMode = "multi";
      impactedMeasureCodesControl?.setValidators([Validators.required, Validators.minLength(1)]);
      impactedMeasureCodeControl?.setValue("");
      impactedMeasureCodeControl?.clearValidators();
    } else if (normalizedCategoryName === INCORRECT_CALCULATION_CATEGORY) {
      this.measureFieldMode = "single";
      impactedMeasureCodeControl?.setValidators([Validators.required]);
      impactedMeasureCodesControl?.setValue([]);
      impactedMeasureCodesControl?.clearValidators();
    } else {
      this.measureFieldMode = "none";
      impactedMeasureCodesControl?.setValue([]);
      impactedMeasureCodesControl?.clearValidators();
      impactedMeasureCodeControl?.setValue("");
      impactedMeasureCodeControl?.clearValidators();
    }

    impactedMeasureCodesControl?.updateValueAndValidity();
    impactedMeasureCodeControl?.updateValueAndValidity();
    this.measureSearchControl.setValue("");

    this.isBiReportsCategory = normalizedCategoryName === BI_REPORTS_CATEGORY;
    if (this.isBiReportsCategory) {
      this.loadPowerBIReportsIfNeeded();
    }
  }

  private loadPowerBIReportsIfNeeded(): void {
    if (this.availableReports.length > 0 || this.isLoadingReports) return;

    this.isLoadingReports = true;
    this.powerBIService
      .getReportNames()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.availableReports = response.reports;
          this.isLoadingReports = false;
        },
        error: () => {
          this.isLoadingReports = false;
        },
      });
  }

  removeImpactedMeasure(measureCode: string): void {
    const control = this.ticketForm.get("impactedMeasureCodes");
    const currentCodes: string[] = control?.value || [];
    control?.setValue(currentCodes.filter((code) => code !== measureCode));
    control?.markAsDirty();
  }

  get filteredMeasureOptions(): ImpactedMeasureOption[] {
    const searchTerm = (this.measureSearchControl.value || "").trim().toLowerCase();
    if (!searchTerm) return this.impactedMeasureOptions;
    return this.impactedMeasureOptions.filter(
      (option) =>
        option.title.toLowerCase().includes(searchTerm) ||
        option.code.toLowerCase().includes(searchTerm)
    );
  }

  getMeasureTitle(measureCode: string): string {
    const option = this.impactedMeasureOptions.find((candidate) => candidate.code === measureCode);
    return option?.title || measureCode;
  }

  private initializeForm(): void {
    this.ticketForm = this.fb.group({
      fullName: ["", Validators.required],
      emailAddress: ["", [Validators.required, Validators.email]],
      organizationName: ["", Validators.required],
      category: [
        {
          value: "",
          disabled: this.data?.lockCategory || false,
        },
        Validators.required,
      ],
      summary: [
        "",
        [
          Validators.required,
          Validators.maxLength(this.VALIDATION_LIMITS.SUMMARY_MAX),
        ],
      ],
      description: [
        "",
        [
          Validators.required,
          Validators.maxLength(this.VALIDATION_LIMITS.DESCRIPTION_MAX),
        ],
      ],
      phiDescription: [
        "",
        [Validators.maxLength(this.VALIDATION_LIMITS.PHI_DESCRIPTION_MAX)],
      ],
      legacyOrganizationId: [0, [Validators.required, Validators.min(1)]],
      impactedMeasureCodes: [[] as string[]],
      impactedMeasureCode: [""],
      reportName: [""],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isFormValid(): boolean {
    if (!this.ticketForm.valid) {
      return false;
    }

    if (this.includePhiSection) {
      const phiDescription = this.ticketForm.get("phiDescription")?.value;
      if (!phiDescription || phiDescription.trim() === "") {
        return false;
      }
    }

    if (this.attachments.some((a) => !a.dataEnrichmentDocumentType || !a.dataEnrichmentDocumentDate ||
        (a.dataEnrichmentDocumentType === 'Other' && !a.dataEnrichmentDocumentTypeOther?.trim()))) {
      return false;
    }

    return true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(Array.from(files));
    }
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.processFiles(Array.from(files));
      event.target.value = "";
    }
  }

  private processFiles(files: File[]): void {
    this.fileErrors = [];

    files.forEach((file) => {
      const validation = this.validateFile(file);
      if (validation.isValid) {
        const attachment: AttachmentFile = {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          dataEnrichmentDocumentType: "",
          dataEnrichmentDocumentTypeOther: "",
          dataEnrichmentDocumentDate: "",
        };
        this.attachments.push(attachment);
      } else {
        this.fileErrors.push(...validation.errors);
      }
    });
  }

  private validateFile(file: File): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (
      !this.SUPPORTED_FILE_TYPES[
        file.type as keyof typeof this.SUPPORTED_FILE_TYPES
      ]
    ) {
      errors.push(`${file.name}: File type not supported`);
    }

    if (file.size > this.MAX_FILE_SIZE) {
      errors.push(
        `${file.name}: File size exceeds ${this.formatFileSize(
          this.MAX_FILE_SIZE
        )} limit`
      );
    }

    if (
      this.attachments.some((a) => a.name === file.name && a.size === file.size)
    ) {
      errors.push(`${file.name}: File already selected`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  removeFile(index: number): void {
    this.attachments.splice(index, 1);
  }

  getFileStatusText(): string {
    if (this.attachments.length === 0) {
      return "No files selected";
    }
    return `${this.attachments.length} file${
      this.attachments.length === 1 ? "" : "s"
    } selected`;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }

  onPhiToggle(): void {
    if (this.includePhiSection) {
      this.ticketForm
        .get("phiDescription")
        ?.setValidators([
          Validators.required,
          Validators.maxLength(this.VALIDATION_LIMITS.PHI_DESCRIPTION_MAX),
        ]);
    } else {
      this.ticketForm
        .get("phiDescription")
        ?.setValidators([
          Validators.maxLength(this.VALIDATION_LIMITS.PHI_DESCRIPTION_MAX),
        ]);
      this.ticketForm.get("phiDescription")?.setValue("");
    }
    this.ticketForm.get("phiDescription")?.updateValueAndValidity();
  }

  createTicket(): void {
    if (!this.isFormValid) {
      return;
    }

    const formValue = this.ticketForm.getRawValue();

    const selectedCategory = this.categories.find(
      (c) => c.name === formValue.category
    );

    if (!selectedCategory) {
      return;
    }

    let fullDescription = formValue.description;
    if (this.includePhiSection && formValue.phiDescription) {
      fullDescription += `\n\nPHI Information:\n${formValue.phiDescription}`;
    }
    if (formValue.reportName) {
      fullDescription += `\n\nRelated Report: ${formValue.reportName}`;
    }

    const createRequest: CreateTicketRequest = {
      organizationId: this.orgId,
      organizationEMRId: this.orgEMRId,
      ticketCategoryId: selectedCategory.id,
      ticketStatusId: 1000,
      subject: formValue.summary,
      description: fullDescription,
      measureCode:
        this.measureFieldMode === "single"
          ? formValue.impactedMeasureCode
          : this.data?.measureCode,
      missingMeasureCodes:
        this.measureFieldMode === "multi" ? formValue.impactedMeasureCodes : undefined,
      patientId: this.data?.patientId,
      assignedUserId: undefined,
      utcOffsetMinutes: new Date().getTimezoneOffset() * -1,
    };

    const attachmentData: AttachmentUploadData[] = this.attachments.map((a) => ({
      file: a.file,
      dataEnrichmentDocumentType: a.dataEnrichmentDocumentType === 'Other'
        ? a.dataEnrichmentDocumentTypeOther
        : a.dataEnrichmentDocumentType,
      dataEnrichmentDocumentDate: a.dataEnrichmentDocumentDate,
    }));

    this.store.dispatch(
      IssueTrackerActions.createTicket({
        ticket: createRequest,
        attachmentData,
        isPhi: this.includePhiSection,
      })
    );
  }

  getOrganizationDisplayValue(): string {
    const formValue = this.ticketForm.value;
    if (formValue.organizationName && formValue.legacyOrganizationId) {
      return `${formValue.organizationName} - ${formValue.legacyOrganizationId}`;
    }
    return formValue.organizationName || "";
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private setAndLockMeasureMappingCategory(): void {
    this.store
      .select(selectTicketCategories)
      .pipe(
        filter((categories) => categories.length > 0),
        take(1)
      )
      .subscribe((categories) => {
        const measureMappingCategory = categories.find(
          (category) => category.name.toLowerCase() === "measure mapping"
        );

        if (measureMappingCategory) {
          this.ticketForm.patchValue({
            category: measureMappingCategory.name,
          });

          this.ticketForm.get("category")?.disable();
        }
      });
  }

  private sortCategoriesWithOtherLast(categories: TicketCategory[]): TicketCategory[] {
    const isOther = (category: TicketCategory) => category.name.toLowerCase() === "other";
    return [
      ...categories.filter((category) => !isOther(category)),
      ...categories.filter(isOther),
    ];
  }

  getCharacterCount(fieldName: string): number {
    const value = this.ticketForm.get(fieldName)?.value || "";
    return value.length;
  }

  getRemainingCharacters(fieldName: string, maxLength: number): number {
    const currentLength = this.getCharacterCount(fieldName);
    return maxLength - currentLength;
  }
  hasFieldError(fieldName: string, errorType: string): boolean {
    const field = this.ticketForm.get(fieldName);
    return !!(
      field &&
      field.hasError(errorType) &&
      (field.dirty || field.touched)
    );
  }

  getFieldErrorMessage(fieldName: string): string {
    const field = this.ticketForm.get(fieldName);
    if (!field || (!field.dirty && !field.touched)) return "";

    if (field.hasError("required")) {
      return `${this.getFieldDisplayName(fieldName)} is required`;
    }
    if (field.hasError("minlength")) {
      const minLength = field.getError("minlength").requiredLength;
      return `${this.getFieldDisplayName(
        fieldName
      )} must be at least ${minLength} characters long`;
    }
    if (field.hasError("maxlength")) {
      const maxLength = field.getError("maxlength").requiredLength;
      return `${this.getFieldDisplayName(
        fieldName
      )} cannot exceed ${maxLength} characters`;
    }
    return "";
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      summary: "Summary",
      description: "Description",
      phiDescription: "PHI Description",
    };
    return displayNames[fieldName] || fieldName;
  }
}
